import { z } from "zod";
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import useResetTfaGoogleCode from "@/domains/playerMenu/hooks/useResetTfaGoogleCode";
import PlayerMenuSettingsSafetyContactWithAdministration from "@/domains/playerMenu/components/settings/safety/tfa/PlayerMenuSettingsSafetyContactWithAdministration";
import PlayerMenuSettingsSafetyResetTfaAuthenticator from "@/domains/playerMenu/components/settings/safety/tfa/PlayerMenuSettingsSafetyResetTfaAuthenticator";

const recoveryCodeSchema = z.preprocess(
  (value) => {
    if (typeof value === "string") {
      return value.replace(/\s/g, ""); // Удаляем пробелы
    }
    return value;
  },
  z
    .string()
    .min(16, "Код должен содержать 16 символов")
    .max(16, "Код должен содержать 16 символов")
    .regex(/^[A-Za-z0-9]+$/, "Recovery code должен содержать только буквы и цифры"),
);

type Props = {
  onBack: () => void;
  onClose: () => void;
};

export default function PlayerMenuSettingsSafetyResetTfaAutheticatorContainer({
  onBack,
  onClose,
}: Props) {
  const [recoveryCode, setRecoveryCode] = useState("");
  const [validationError, setValidationError] = useState("");
  const [isRecoveryAccount, setIsRecoveryAccount] = useState(false);
  const { checkCodeHandler, checkIsCompleted, checkIsPending, checkError } =
    useResetTfaGoogleCode();

  useEffect(() => {
    if (recoveryCode) {
      try {
        setValidationError("");
        recoveryCodeSchema.parse(recoveryCode);
      } catch (error) {
        if (error instanceof z.ZodError) {
          setValidationError(error.errors[0].message);
        }
      }
    } else {
      setValidationError("");
    }
  }, [recoveryCode]);

  const onSubmit = useCallback(
    async (e: SyntheticEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (recoveryCode.length === 6 && !checkIsPending && !validationError) {
        checkCodeHandler(recoveryCode);
      }
    },
    [checkCodeHandler, checkIsPending, recoveryCode, validationError],
  );

  useEffect(() => {
    if (recoveryCode.length === 6 && !checkIsPending && !validationError) {
      checkCodeHandler(recoveryCode);
    }
  }, [checkCodeHandler, recoveryCode, checkIsPending, validationError]);

  useEffect(() => {
    if (checkIsCompleted) {
      onClose();
    }
  }, [onClose, checkIsCompleted]);

  const onResetAccess = useCallback(() => {
    setIsRecoveryAccount(true);
  }, []);

  const backToInputRecoveryCode = useCallback(() => {
    setIsRecoveryAccount(false);
  }, []);

  const disableSubmitButton = useMemo(
    () => !!validationError || !recoveryCode,
    [validationError, recoveryCode],
  );

  const error = useMemo(() => validationError || checkError, [validationError, checkError]);

  if (checkIsCompleted) {
    return null;
  }

  if (isRecoveryAccount) {
    return (
      <PlayerMenuSettingsSafetyContactWithAdministration
        onSubmit={() => {
          // TODO:
        }}
        onBack={backToInputRecoveryCode}
      />
    );
  }

  return (
    <PlayerMenuSettingsSafetyResetTfaAuthenticator
      onBack={onBack}
      error={error}
      code={recoveryCode}
      onCodeChange={setRecoveryCode}
      onSubmit={onSubmit}
      pending={checkIsPending}
      onResetAccess={onResetAccess}
      disableSubmitButton={disableSubmitButton}
    />
  );
}
